<template>
  <div>
    <b-modal
      id="product-modal"
      hide-header
      hide-footer
      no-stacking
      centered 
      size="lg"
      @hidden="closeModal"
      @show="onShow"
    >
      <div class="modal-header">
        <div class="modal-title">Cadastro de produto</div>
        <span class="icon-box"><v-close class="icon stroke" @click="closeModal"  /></span>
      </div>

      <div class="modal-body p-3">
        <validation-observer ref="productForm">
          <div class="row">
            <div class="col-12 mb-3">
              <p class="sub-title">Mat/med</p>
            </div>
            <div class="col-4 mb-3">
              <b-form-group>
                <label for="table_mat_med" class="title">Tabela</label>
                <span class="help optional">{{ !configuration.required_fields?.table_mat_med ? '(opcional)' : '*' }}</span>
                <multiselect
                  id="table_mat_med"
                  autocomplete="off"
                  v-model="form.table_mat_med"
                  @select="onSelectedMatMedTable"
                  :options="mat_med_tables"
                  :option-height="40"
                  :showLabels="false"
                  :searchable="true"
                  placeholder="Selecionar"
                  class="with-border"
                  ref="table_mat_med"
                >
                  <template slot="caret">
                    <div class="chevron">
                      <v-chevron-down />
                    </div>
                  </template>
                  <template slot="noOptions"> Nenhuma opção </template>
                  <template slot="noResult"> Nenhum resultado </template>
                </multiselect>
                <div v-if="errors.table_mat_med" class="custom-invalid-feedback">Campo obrigatório</div>
              </b-form-group>            
            </div>
            <div class="col-8 mb-3">
              <b-form-group
              >
                <label for="material_medicine_id" class="title">Produto</label>
                <span class="help optional">{{ !configuration.required_fields?.table_mat_med && !form.table_mat_med ? '(opcional)' : '*' }}</span>
                <multiselect
                  track-by="id"
                  class="with-border"
                  placeholder="Selecionar"
                  v-model="selectedProcedureMatMed"
                  :options="proceduresMatMed"
                  :showLabels="false"
                  :internalSearch="false"
                  :searchable="true"
                  @search-change="handleSearchMatMed"
                  @select="onSelectMatMed"
                  ref="material_medicine_id"
                  :disabled="!form.table_mat_med"
                >
                  <template slot="caret">
                    <div class="chevron">
                        <v-chevron-down />
                    </div>
                  </template>
                  <template slot="singleLabel" slot-scope="{ option }">
                    <OptionLineSimpro v-if="option.type === 'Simpro'" :simproProps="option.simpro" />
                    <OptionLineBrasindice v-if="option.type === 'Brasindice'" :brasindiceProps="option.brasindice" />
                  </template>
                  <template slot="option" slot-scope="{ option }">
                    <OptionSimpro v-if="option.type === 'Simpro'" :simproProps="option.simpro" />
                    <OptionBrasindice v-if="option.type === 'Brasindice'" :brasindiceProps="option.brasindice" />
                  </template>
                  <template slot="noOptions">
                    Nenhuma opção
                  </template>
                  <template slot="noResult">
                    Nenhum resultado, verifique se o item pesquisa foi importado para o sistema.
                  </template>
                </multiselect>
                <div v-if="errors.material_medicine_id" class="custom-invalid-feedback">Campo obrigatório</div>
              </b-form-group>
            </div>
            <div class="col-4 mb-3">
              <b-form-group>
                <label for="mat_med_code" class="title">Código</label>
                <b-form-input
                  trim
                  id="mat_med_code"
                  autocomplete="off"
                  placeholder="Descrever"
                  v-model="mat_med.code"
                  :disabled="true"
                  @keyup="handleInputOnlyNumbers"
                />
              </b-form-group>
            </div>
            <div class="col-2 mb-3">
              <b-form-group>
                <label for="mat_med_pmc" class="title">PMC</label>
                <b-form-input
                  trim
                  id="mat_med_pmc"
                  autocomplete="off"
                  placeholder="R$ 0,00"
                  v-model="mat_med.pmc"
                  ref="mat_med_pmc"
                  :disabled="true"
                />
              </b-form-group>
            </div>
            <div class="col-2 mb-3">
              <b-form-group>
                <label for="mat_med_pmc_fracao" class="title">PMC Fração</label>
                <b-form-input
                  trim
                  id="mat_med_pmc_fracao"
                  autocomplete="off"
                  placeholder="R$ 0,00"
                  v-model="mat_med.pmc_fracao"
                  ref="mat_med_pmc_fracao"
                  :disabled="true"
                />
              </b-form-group>
            </div>
            <div class="col-2 mb-3">
              <b-form-group>
                <label for="mat_med_pf" class="title">PF</label>
                <b-form-input
                  trim
                  id="mat_med_pf"
                  autocomplete="off"
                  placeholder="R$ 0,00"
                  v-model="mat_med.pf"
                  ref="mat_med_pf"
                  :disabled="true"
                />
              </b-form-group>
            </div>
            <div class="col-2 mb-3">
              <b-form-group>
                <label for="mat_med_pf_fracao" class="title">PF Fração</label>
                <b-form-input
                  trim
                  id="mat_med_pf_fracao"
                  autocomplete="off"
                  placeholder="R$ 0,00"
                  v-model="mat_med.pf_fracao"
                  ref="mat_med_pf_fracao"
                  :disabled="true"
                />
              </b-form-group>
            </div>
          </div>

          <hr/>

          <div class="row">
            <div class="col-12 mb-3">
              <p class="sub-title">Dados principais</p>
            </div>
            <div class="col-8 mb-3">
              <b-form-group
                :invalid-feedback="'Campo obrigatório'"
                :state="errors.name ? false : null"
              >
                <label for="name" class="title">Nome do produto</label>
                <span class="help optional">*</span>
                <b-form-input
                  trim
                  id="name"
                  autocomplete="off"
                  placeholder="Descrever"
                  v-model="form.name"
                  ref="name"
                />
              </b-form-group>
            </div>
            <div class="col-4 mb-3">
              <b-form-group
              >
                <label for="active_ingredient" class="title">Princípio ativo</label>
                <span class="help optional">{{ !configuration.required_fields?.active_ingredient ? '(opcional)' : '*' }}</span>
                <b-form-input
                  trim
                  id="active_ingredient"
                  autocomplete="off"
                  placeholder="Descrever"
                  v-model="form.active_ingredient"
                  ref="active_ingredient"
                />
                <div v-if="errors.active_ingredient" class="custom-invalid-feedback">Campo obrigatório</div>
              </b-form-group>
            </div>
            <div class="col-6 mb-3">
              <b-form-group
                :invalid-feedback="'Campo obrigatório'"
                :state="errors.sku_code ? false : null"
              >
                <label for="sku_code" class="title">Código SKU</label>
                <span class="help optional">{{ !configuration.required_fields?.sku_code ? '(opcional)' : '*' }}</span>
                <b-form-input
                  trim
                  id="sku_code"
                  autocomplete="off"
                  placeholder="Descrever"
                  v-model="form.sku_code"
                  ref="sku_code"
                  @keyup="handleInputOnlyNumbers"
                />
              </b-form-group>
            </div>
            <div class="col-6 mb-3">
              <b-form-group
                :invalid-feedback="'Campo obrigatório'"
              >
                <label for="number" class="title">Código Interno</label>
                <span class="help optional">*</span>
                <b-form-input
                  trim
                  id="number"
                  autocomplete="off"
                  placeholder="Descrever"
                  v-model="form.number"
                  ref="number"
                  :disabled="true"
                />
              </b-form-group>
            </div>

            <div class="col-4 mb-3">
              <b-form-group>
                <label for="purchasing_unit_id" class="title">Unidade de compra</label>
                <span class="help optional">*</span>
                <multiselect
                  :state="errors.purchasing_unit_id ? false : null"
                  id="purchasing_unit_id"
                  v-model="form.purchasing_unit"
                  track-by="id"
                  label="label"
                  placeholder="Selecionar"
                  :options="measureUnits"
                  :allow-empty="false"
                  :option-height="40"
                  :showLabels="false"
                  :showNoResults="false"
                  class="with-border"
                  :disabled="false"
                  ref="purchasing_unit_id"
                  @select="el => ( this.form.purchasing_unit_id = el.id )"
                >
                  <template slot="caret">
                    <div class="chevron">
                      <v-chevron-down />
                    </div>
                  </template>

                  <template slot="singleLabel" slot-scope="{ option }">
                    {{ option.short_name }} - {{ option.name}}
                  </template>

                  <template slot="noOptions">
                    Nenhuma opção
                  </template>

                  <template slot="noResult">
                    Nenhum resultado
                  </template>
                </multiselect>
                <div v-if="errors.purchasing_unit_id" class="custom-invalid-feedback">Campo obrigatório</div>
              </b-form-group>
            </div>

            <div class="col-4 mb-3">
              <b-form-group>
                <label for="sales_unit_id" class="title">Unidade de Venda</label>
                <span class="help optional">*</span>
                <multiselect
                  :state="errors.sales_unit_id ? false : null"
                  id="sales_unit_id"
                  v-model="form.sales_unit"
                  track-by="id"
                  label="label"
                  placeholder="Selecionar"
                  :options="measureUnits"
                  :allow-empty="false"
                  :option-height="40"
                  :showLabels="false"
                  :showNoResults="false"
                  class="with-border"
                  :disabled="false"
                  ref="sales_unit_id"
                  @select="el => ( this.form.sales_unit_id = el.id )"
                >
                  <template slot="caret">
                    <div class="chevron">
                      <v-chevron-down />
                    </div>
                  </template>

                  <template slot="singleLabel" slot-scope="{ option }">
                    {{ option.short_name }} - {{ option.name}}
                  </template>

                  <template slot="noOptions">
                    Nenhuma opção
                  </template>

                  <template slot="noResult">
                    Nenhum resultado
                  </template>
                </multiselect>
                <div v-if="errors.sales_unit_id" class="custom-invalid-feedback">Campo obrigatório</div>
              </b-form-group>
            </div>

            <div class="col-4 mb-3">
              <b-form-group
                id="field_barcode">
                <label for="barcode" class="title">
                  Relação compra/venda 
                  <span class="help optional">*</span>
                  <span class="icon-box help-icon" v-b-tooltip.hover title="Relação entre a quantidade comprada e a vendida, como 1:1 (compra e venda iguais), 2:1 (compra o dobro do que vende) ou 1:2 (compra 1 e vende 2)">
                    <HelpCircle class="icon stroke" />
                  </span>                  
                </label>
                <div class="input-group">
                  <b-form-input
                    :state="errors.buy_ratio ? false : null"
                    id="buy_ratio"
                    class="mr-2"
                    trim
                    autocomplete="off"
                    placeholder="1"
                    v-model="form.buy_ratio"
                    @keyup="handleInputOnlyNumbers"
                    ref="buy_ratio"
                  />
                  <b-form-input
                    :state="errors.sell_ratio ? false : null"
                    id="sell_ratio"
                    trim
                    autocomplete="off"
                    placeholder="1"
                    v-model="form.sell_ratio"
                    @keyup="handleInputOnlyNumbers"
                    ref="sell_ratio"
                  />
                </div>
                <div v-if="errors.buy_ratio || errors.sell_ratio" class="custom-invalid-feedback">Campo obrigatório</div>
              </b-form-group>
            </div>

            <div class="col-4 mb-3">
              <b-form-group>
                <label for="product_category_id" class="title">Categoria</label>
                <span class="help optional">*</span>
                <multiselect
                  id="product_category_id"
                  autocomplete="off"
                  ref="product_category_id"
                  v-model="selectedCategory"
                  @input="changeSelectedCategory"
                  :options="categories"
                  track-by="id"
                  label="name"
                  :option-height="40"
                  :showLabels="false"
                  :searchable="true"
                  placeholder="Selecionar"
                  class="with-border"
                >
                  <template slot="caret">
                    <div class="chevron">
                      <v-chevron-down />
                    </div>
                  </template>
                  <template slot="noOptions"> Nenhuma opção </template>
                  <template slot="noResult"> Nenhum resultado </template>
                </multiselect>
                <div v-if="errors.product_category_id" class="custom-invalid-feedback">Campo obrigatório</div>
              </b-form-group>
            </div>

            <div class="col-4 mb-3">
              <b-form-group>
                <label for="product_class_id" class="title">Classe</label>
                <span class="help optional">*</span>
                <multiselect
                  id="product_class_id"
                  autocomplete="off"
                  ref="product_class_id"
                  v-model="selectedClass"
                  @input="changeSelectedClass"
                  :options="classes"
                  track-by="id"
                  label="name"
                  :option-height="40"
                  :showLabels="false"
                  :searchable="true"
                  placeholder="Selecionar"
                  class="with-border"
                >
                  <template slot="caret">
                    <div class="chevron">
                      <v-chevron-down />
                    </div>
                  </template>
                  <template slot="noOptions"> Nenhuma opção </template>
                  <template slot="noResult"> Nenhum resultado </template>
                </multiselect>
                <div v-if="errors.product_class_id" class="custom-invalid-feedback">Campo obrigatório</div>
              </b-form-group>
            </div>

            <div class="col-4 mb-3">
              <b-form-group>
                <label for="product_subclass_id" class="title">Subclasse</label>
                <span class="help optional">{{ !configuration.required_fields?.product_subclass_id ? '(opcional)' : '*' }}</span>
                <multiselect
                  id="product_subclass_id"
                  autocomplete="off"
                  ref="product_subclass_id"
                  v-model="selectedSubclass"
                  @input="changeSelectedSubclass"
                  :options="subclasses"
                  track-by="id"
                  label="name"
                  :option-height="40"
                  :showLabels="false"
                  :searchable="true"
                  placeholder="Selecionar"
                  class="with-border"
                >
                  <template slot="caret">
                    <div class="chevron">
                      <v-chevron-down />
                    </div>
                  </template>
                  <template slot="noOptions"> Nenhuma opção </template>
                  <template slot="noResult"> Nenhum resultado </template>
                </multiselect>
                <div v-if="errors.product_subclass_id" class="custom-invalid-feedback">Campo obrigatório</div>
              </b-form-group>
            </div>
            
            <div class="col-3 mb-3">
              <b-form-group
                id="cost_price"
              >
                <label for="cost_price" class="title">Preço de Custo</label>
                <span class="help optional">{{ !configuration.required_fields?.cost_price ? '(opcional)' : '*' }}</span>
                <money 
                  id="cost_price"
                  v-model="form.cost_price" 
                  class="form-control"
                  :state="errors.cost_price ? false : null"
                  ref="cost_price"
                />
                <b-form-invalid-feedback v-if="errors?.cost_price" class="custom-invalid-feedback">
                  {{ errors.cost_price }}
                </b-form-invalid-feedback>
              </b-form-group>
            </div>

            <div class="col-3 mb-3">
              <b-form-group
                id="sale_price"
              >
                <label for="sale_price" class="title">Preço de Venda</label>
                <span class="help optional">{{ !configuration.required_fields?.sale_price ? '(opcional)' : '*' }}</span>
                <money 
                  id="sale_price"
                  v-model="form.sale_price" 
                  class="form-control"
                  :state="errors.sale_price ? false : null"
                  ref="sale_price"
                />
                <b-form-invalid-feedback v-if="errors?.sale_price" class="custom-invalid-feedback">
                  {{ errors.sale_price }}
                </b-form-invalid-feedback>
              </b-form-group>
            </div>

            <div class="col-3 mb-3">
              <b-form-group
                id="field_expiration_notice"
                :invalid-feedback="'Campo obrigatório'"
                :state="isValidStateNumber(form.expiration_notice)"
              >
                <label for="expiration_notice" class="title">Aviso de vencimento</label>
                <span class="help optional">*</span>
                <b-form-input
                  :state="errors.expiration_notice ? false : null"
                  autocomplete="off"
                  id="expiration_notice"
                  trim
                  placeholder="Dias"
                  type="number"
                  min="0"
                  v-model="form.expiration_notice"
                  @input="value => { this.form = {...this.form, expiration_notice: !!value && Math.abs(value) >= 0 ? Math.abs(value) : null } }"
                  ref="expiration_notice"
                />
              </b-form-group>
            </div>

            <div class="col-3 mb-3">
              <b-form-group
                id="field_minimum_quantity"
                :invalid-feedback="'Campo obrigatório'"
                :state="isValidStateNumber(form.minimum_quantity)"
              >
                <label for="minimum_quantity" class="title">Quantidade mínima</label>
                <span class="help optional">*</span>
                <b-form-input
                  :state="errors.minimum_quantity ? false : null"
                  autocomplete="off"
                  id="minimum_quantity"
                  trim
                  type="number"
                  min="0"
                  v-model="form.minimum_quantity"
                  placeholder="Quantidade"
                  ref="minimum_quantity"
                  @input="value => { this.form = { ...this.form, minimum_quantity: !!value && Math.abs(value) >= 0 ? Math.abs(value) : null } }"
                />
              </b-form-group>
            </div>

            <div class="col-12 mb-3">
              <div class="card" id="barcodes">
                <div class="card-header p-2">
                  Cód. de barras
                  <span class="help optional">{{ !configuration.required_fields?.barcode ? '(opcional)' : '*' }}</span>
                </div>
                <div class="card-body p-0">
                  <div class="input-group">
                    <b-form-input
                      id="newBarcode"
                      trim
                      autocomplete="off"
                      placeholder="Código de barras"
                      v-model="newBarcode"
                      @keyup="handleInputOnlyNumbers"
                    />
                    <div class="input-group-append">
                      <Icon>
                        <Plus class="icon-plus" @click="addBarcode"/>
                      </Icon>
                    </div>
                  </div>
                  
                  <div v-for="(barcode, indx) in form.barcodes" :key="'barcode-' + indx" class="input-group">
                    <b-form-input
                      id="newBarcode"
                      trim
                      autocomplete="off"
                      placeholder="Código de barras"
                      v-model="form.barcodes[indx]['barcode']"
                      :disabled="!form.barcodes[indx].editable"
                      @change="handleChangeBarcode(indx)"
                    />
                    <div class="input-group-append">
                      <Icon>
                        <Edit class="icon icon-edit" @click="editBarcode(indx)"/>
                      </Icon>
                      <Icon>
                        <Trash class="icon icon-trash"  @click="deleteBarcode(indx)"/>
                      </Icon>
                    </div>
                  </div>
                </div>
              </div>
              <div 
                  v-if="errors.barcode ? true : false"
                  class="custom-invalid-feedback"
                >
                Campo obrigatório
              </div>
            </div>

            <div class="col-6 mb-3">
              <b-form-group
                id="psychotropic_medication"
              >
                <label for="psychotropic_medication" class="title">
                  Medicamento psicotrópico
                  <span class="help optional">*</span>
                  <span class="icon-box help-icon" v-b-tooltip.hover title="Substâncias que atuam no sistema nervoso central, alterando o funcionamento cerebral. Possuem uso controlado devido ao risco de dependência e efeitos colaterais.">
                    <HelpCircle class="icon stroke" />
                  </span> 
                </label>
                <b-form-radio-group
                  id="psychotropic_medication"
                  v-model="form.psychotropic_medication"
                  name="psychotropic_medication"
                  :disabled="!userHasPermission('EstMedPsi1')"
                >
                  <b-form-radio value="1">Sim</b-form-radio>
                  <b-form-radio value="0">Não</b-form-radio>
                </b-form-radio-group>
                <div 
                  v-if="errors.psychotropic_medication ? true : false"
                  class="custom-invalid-feedback"
                >
                  Campo obrigatório
                </div>
              </b-form-group>
            </div>

            <div class="col-6 mb-3">
              <b-form-group
                id="cold_chain_medicine"
              >
                <label for="cold_chain_medicine" class="title">
                  Medicamento de cadeia fria
                  <span class="help optional">*</span>
                  <span class="icon-box help-icon" v-b-tooltip.hover title="Medicamentos que exigem armazenamento e transporte em temperaturas rigorosamente controladas para garantir sua eficácia e segurança.">
                    <HelpCircle class="icon stroke" />
                  </span> 
                </label>
                <b-form-radio-group
                  id="cold_chain_medicine"
                  v-model="form.cold_chain_medicine"
                  name="cold_chain_medicine"
                  @change="handleChangeColdChainMedicine"
                >
                  <b-form-radio value="1">Sim</b-form-radio>
                  <b-form-radio value="0">Não</b-form-radio>
                </b-form-radio-group>
                <div 
                  v-if="errors.cold_chain_medicine ? true : false"
                  class="custom-invalid-feedback"
                >
                  Campo obrigatório
                </div>
              </b-form-group>
            </div>

            <div class="col-4 mb-3">
              <b-form-group
                id="temperature"
              >
                <label for="temperature" class="title">
                  Temperatura
                  <span class="help optional">{{ !form.cold_chain_medicine || form.cold_chain_medicine === "0" ? '(opcional)' : '*' }}</span>
                </label>
                <b-form-input
                  :state="errors.temperature ? false : null"
                  id="temperature"
                  trim
                  type="text"
                  @keyup="formatTemperature"
                  autocomplete="off"
                  placeholder="Descrever"
                  v-model="form.temperature"
                  ref="temperature"
                  :disabled="!form.cold_chain_medicine || form.cold_chain_medicine === '0'"
                />
                <div 
                  v-if="errors.temperature ? true : false"
                  class="custom-invalid-feedback"
                >
                  {{ errors.temperature }}
                </div>
              </b-form-group>
            </div>

          </div>

          <hr/>

          <div class="row">
            <div class="col-12 mb-3">
              <p class="sub-title">Demais informações</p>
            </div>
            <div class="col-6 mb-3">
              <b-form-group>
                <label for="expense_code" class="title">Cód. despesa</label>
                <span class="help optional">{{ !configuration.required_fields?.expense_code ? '(opcional)' : '*' }}</span>
                <multiselect
                  id="expense_code"
                  autocomplete="off"
                  v-model="selectedExpenseCode"
                  @input="changeSelectedExpenseCode"
                  :options="expenseCodeOptions"
                  track-by="code"
                  label="label"
                  :option-height="40"
                  :showLabels="false"
                  :searchable="true"
                  placeholder="Selecionar"
                  class="with-border"
                >
                  <template slot="caret">
                    <div class="chevron">
                      <v-chevron-down />
                    </div>
                  </template>
                  <template slot="noOptions"> Nenhuma opção </template>
                  <template slot="noResult"> Nenhum resultado </template>
                </multiselect>
                <div v-if="errors.expense_code" class="custom-invalid-feedback">Campo obrigatório</div>
              </b-form-group>
            </div>
            <div class="col-6 mb-3">
              <b-form-group
                :state="errors.table ? false : null"
              >
                <label for="table" class="title">Tabela</label>
                <span class="help optional">{{ !configuration.required_fields?.table ? '(opcional)' : '*' }}</span>
                <multiselect
                  id="table"
                  autocomplete="off"
                  ref="table"
                  v-model="selectedTable"
                  @input="changeSelectedTable"
                  :options="tableOptions"
                  track-by="code"
                  label="label"
                  :option-height="40"
                  :showLabels="false"
                  :searchable="true"
                  placeholder="Selecionar"
                  class="with-border"
                >
                  <template slot="caret">
                    <div class="chevron">
                      <v-chevron-down />
                    </div>
                  </template>
                  <template slot="noOptions"> Nenhuma opção </template>
                  <template slot="noResult"> Nenhum resultado </template>
                </multiselect>
                <div v-if="errors.table" class="custom-invalid-feedback">Campo obrigatório</div>
              </b-form-group>
            </div>
            <div class="col-6 mb-3">
              <b-form-group>
                <label for="criticality_level" class="title">Nível de criticidade</label>
                <span class="help optional">{{ !configuration.required_fields?.criticality_level ? '(opcional)' : '*' }}</span>
                <multiselect
                  id="criticality_level"
                  autocomplete="off"
                  v-model="selectedCriticalityLevel"
                  @input="changeSelectedCriticalityLevel"
                  :options="criticalityLevelOptions"
                  track-by="code"
                  label="label"
                  :option-height="40"
                  :showLabels="false"
                  :searchable="true"
                  placeholder="Selecionar"
                  class="with-border"
                >
                  <template slot="caret">
                    <div class="chevron">
                      <v-chevron-down />
                    </div>
                  </template>
                  <template slot="noOptions"> Nenhuma opção </template>
                  <template slot="noResult"> Nenhum resultado </template>
                </multiselect>
                <div v-if="errors.criticality_level" class="custom-invalid-feedback">Campo obrigatório</div>
              </b-form-group>
            </div>
            <div class="col-6 mb-3">
              <b-form-group>
                <label for="manufacturer_id" class="title">Fabricante</label>
                <span class="help optional">{{ !configuration.required_fields?.manufacturer_id ? '(opcional)' : '*' }}</span>
                <multiselect
                  id="manufacturer_id"
                  autocomplete="off"
                  ref="manufacturer"
                  v-model="selectedManufacturer"
                  @input="changeSelectedManufacturer"
                  :options="manufacturers"
                  track-by="id"
                  label="name"
                  :option-height="40"
                  :showLabels="false"
                  :searchable="true"
                  placeholder="Selecionar"
                  class="with-border"
                >
                  <template slot="caret">
                    <div class="chevron">
                      <v-chevron-down />
                    </div>
                  </template>
                  <template slot="noOptions"> Nenhuma opção </template>
                  <template slot="noResult"> Nenhum resultado </template>
                </multiselect>
                <div 
                  v-if="errors.manufacturer_id ? true : false"
                  class="custom-invalid-feedback"
                >
                  Campo obrigatório
                </div>
              </b-form-group>
            </div>
            
            <div class="col-12 mb-3">
              <b-form-group
                id="field_details"
                :state="errors.details ? false : null"
              >
                <label for="details" class="title">
                  Observações
                  <span class="help optional">{{ !configuration.required_fields?.details ? '(opcional)' : '*' }}</span>
                </label>
                <textarea
                  v-model="form.details"
                  rows="3"
                  class="form-control"
                  ref="details"
                  placeholder="Descrever"
                ></textarea>
                <div 
                  v-if="errors.details ? true : false"
                  class="custom-invalid-feedback"
                >
                  Campo obrigatório
                </div>
              </b-form-group>
            </div>
          </div>

        </validation-observer>

        <div v-if="!form.id" class="md-button">
          <b-button
            class="wh-button button"
            variant="primary"
            size="lg"
            @click="createProduct"
          >
              Salvar produto
          </b-button>
        </div>

        <div v-else class="md-button">
          <b-button
            class="wh-button button"
            variant="primary"
            size="lg"
            @click="updateProduct"
            :disabled="!userHasPermission('EstMedPsi4')"
          >
              Atualizar produto
          </b-button>
        </div>
      </div>

    </b-modal>
  </div>
</template>
<script>
import Close from '@/assets/icons/close.svg'
import ChevronDown from '@/assets/icons/chevron-down.svg'
import { EventBus } from '@/utils/eventBus';
import apiMatMed from '@matMed/api'
import { parseNumberToMoney } from "@/utils/moneyHelper";
import {
  userHasPermission,
} from '@/utils/localStorageManager'

export default {
  name: 'ProductModal',
  components: {
    'v-close': Close,
    'v-chevron-down': ChevronDown,
    Icon: () => import('@/components/General/Icon'),
    Plus: () => import('@/assets/icons/icon-plus.svg'),
    Trash: () => import('@/assets/icons/trash.svg'),
    Edit: () => import('@/assets/icons/edit.svg'),
    HelpCircle: () => import('@/assets/icons/help-circle.svg'),
    OptionLineSimpro: () => import('@matMed/components/OptionLineSimpro'),
    OptionLineBrasindice: () => import('@matMed/components/OptionLineBrasindice'),
    OptionSimpro: () => import('@matMed/components/OptionSimpro'),
    OptionBrasindice: () => import('@matMed/components/OptionBrasindice'),
  },
  props: {
    product: Object,
  },
  mounted() {
    this.api.getMeasureUnits()
      .then(res => {
        this.measureUnits = res.data.map(el => ({ ...el, label: `${el.short_name} - ${el.name}` }));
      });

    this.api.getWarehouseProductCategories(this.clinic.id)
      .then(res => {
        this.categories = res.data.filter(category => category.status.enable === true);
      });

    this.api.getWarehouseManufacturers(this.clinic.id, {status: true})
      .then(res => {
        this.manufacturers = res.data;
      });
  },
  data() {
    return {
      clinic: JSON.parse(localStorage.getItem('clinic')),
      configuration: {},
      measureUnits: [],
      validated: false,
      errors: {},
      form: {
        table_mat_med: null,
        psychotropic_medication: '0',
        cold_chain_medicine: '0',
        barcodes: [],
        buy_ratio: 1,
        sell_ratio: 1
      },
      mat_med: {
        code: '',
        pmc: '',
        pmc_fracao: '',
        pf: '',
        pf_fracao: '',
      },
      batchControlOptions: [{ label: 'Sim', value: true }, { label: 'Não', value: false }],
      categories: [],
      classes: [],
      mat_med_tables: [
        'Brasindice',
        'Simpro'
      ],
      subclasses: [],
      selectedCategory: null,
      selectedClass: null,
      selectedSubclass: null,
      proceduresMatMed: [],
      selectedProcedureMatMed: {},
      selectedExpenseCode: {},
      expenseCodeOptions: [
        { code: '01', label: '01 - Gases medicinais' },
        { code: '02', label: '02 - Medicamentos' },
        { code: '03', label: '03 - Materiais' },
        { code: '05', label: '05 - Diárias' },
        { code: '07', label: '07 - Taxas e aluguéis' },
        { code: '08', label: '08 - OPME' },
      ],
      selectedTable: {},
      tableOptions: [
        { code: '00', label: '00 - Tabela própria das operadoras' },
        { code: '18', label: '18 - Diárias, taxas e gases medicinais' },
        { code: '19', label: '19 - Materias e Órteses, Próteses e Materiais Especiais (OPME)' },
        { code: '20', label: '20 - Medicamentos' },
        { code: '22', label: '22 - Procedimentos e eventos em saúde' },
        { code: '90', label: '90 - Tabela Própria Pacote Odontológico' },
        { code: '98', label: '98 - Tabela Própria de Pacotes' },        
      ],
      selectedCriticalityLevel: {},
      criticalityLevelOptions: [
        { code: 'X', label: 'X - Pouco crítico, sua falta não interrompe as atividades.' },        
        { code: 'Y', label: 'Y - Crítico, sua falta prejudica somente a qualidade das atividades.' },        
        { code: 'Z', label: 'Z - Muito crítico, sua falta paralisa as atividades.' },        
      ],
      selectedManufacturer: {},
      manufacturers: [],
      newBarcode: null,
    }
  },
  methods: {
    userHasPermission,
    async onShow() {
      await this.getWarehouseConfigurations();

      if(!this.form.id){
        this.getInternalCode();
      }
    },
    async getWarehouseConfigurations() {
      this.api.getWarehouseConfigurations(this.clinic.id)
      .then(res => {
        this.configuration = res.data;
        if (res.data.batch_control_required) {
          this.form.batch_control = { label: 'Sim', value: true };
        }
      })
      .catch(err => {
        this.$toast.error(err.message);
      })
    },
    async getInternalCode() {
      this.api.getWarehouseInternalCode(this.clinic.id)
      .then(res => {
        this.form.number = res.data.number;
      })
      .catch(err => {
        this.$toast.error(err.message);
      })
    },
    createProduct() {
      if (!this.isValidForm()) {
        this.$toast.error("Preencha os campos obrigatórios.");
        return;
      }

      const props = {
        clinic_id: this.clinic.id,
        name: this.form.name,
        barcode: this.form.barcode,
        // measure_unit_id: this.form.measure_unit.id,
        table_mat_med: this.form.table_mat_med,
        material_medicine_id: this.form.material_medicine_id,
        expiration_notice: this.form.expiration_notice,
        minimum_quantity: this.form.minimum_quantity,
        batch_control: this.form.batch_control?.value,
        details: this.form.details,
        product_category_id: this.selectedCategory?.id,
        product_class_id: this.selectedClass?.id,
        product_subclass_id: this.selectedSubclass?.id,
        active_ingredient: this.form.details,
        sku_code: this.form.sku_code,
        internal_code: this.form.internal_code,
        purchasing_unit_id: this.form.purchasing_unit_id,
        sales_unit_id: this.form.sales_unit_id,
        buy_ratio: this.form.buy_ratio,
        sell_ratio: this.form.sell_ratio,
        psychotropic_medication: this.form.psychotropic_medication,
        cold_chain_medicine: this.form.cold_chain_medicine,
        temperature: this.form.temperature,
        expense_code: this.form.expense_code,
        table: this.form.table,
        criticality_level: this.form.criticality_level,
        manufacturer_id: this.form.manufacturer_id,
        barcodes: this.form.barcodes,
        number: this.form.number,
        cost_price: this.form.cost_price,
        sale_price: this.form.sale_price,
        
      };
      const isLoading = this.$loading.show();
      this.api.createProduct(props)
      .then(() => {
        this.$toast.success('Produto cadastrado com sucesso!');
        EventBus.$emit('reloadProducts');
        this.$bvModal.hide('product-modal');
        this.clearForm();        
      })
      .catch(err => {
        this.$toast.error(err.message);
      })
      .finally(() => isLoading.hide());
    },

    updateProduct() {
      if (!this.isValidForm()) return;
      const isLoading = this.$loading.show()
      this.api.updateProduct(this.form.id, this.form)
      .then(() => {
        this.clearForm();
        this.$toast.success('Produto atualizado com sucesso!');
        this.$bvModal.hide('product-modal');
        this.$emit('onHide', true);
        EventBus.$emit('reloadProducts');
      })
      .catch(err => {
        this.$toast.error(err.message);
      })
      .finally(() => isLoading.hide());
    },

    closeModal() {
      this.clearForm();
      this.$bvModal.hide('product-modal');
    },

    clearForm() {
      this.form = {
        batch_control: this.form.batch_control,
        psychotropic_medication: "0",
        cold_chain_medicine: "0",
        buy_ratio: 1,
        sell_ratio: 1,
        barcodes: [],
      };

      this.errors = {};

      this.mat_med = {};

      this.selectedProcedureMatMed = null;
      this.selectedManufacturer = null;
      this.selectedCategory = null;
      this.selectedClass = null;
      this.selectedSubclass = null;
      this.selectedCriticalityLevel = null;
      this.selectedExpenseCode = null;
      this.selectedTable = null;
      this.selectedManufacturer = null;

      this.newBarcode = null;

      this.validated = false;
    },

    isValidForm() {
      this.validated = true;
      
      let isValid = true;

      this.errors = {};

      if(this.newBarcode){
        this.$toast.warning('Para cadastrar um código de barras, clique no botão "Adicionar"');
        isValid = false;
      }

      const requiredFields = [
        'name', 
        'number',
        'purchasing_unit_id',
        'sales_unit_id',        
        'buy_ratio',
        'sell_ratio',
        'product_category_id',
        'product_class_id',
        'expiration_notice',
        'minimum_quantity',
        'psychotropic_medication',
      ];
      
      requiredFields.map(item => {
        if(this.$refs[item] && (!this.$refs[item].value || this.$refs[item].value === '' || this.$refs[item].value.length === 0)){
          isValid = false;
          this.errors[item] = "Campo obrigatório";
        }
      });
      
      if(this.configuration?.required_fields){
        Object.keys(this.configuration.required_fields).map(item => {
          if(this.$refs[item] && (!this.$refs[item].value || this.$refs[item].value.length === 0) && this.configuration.required_fields[item]){
            isValid = false;
            this.errors[item] = "Campo obrigatório";
          }
        });
      }

      if(this.configuration?.required_fields?.barcode){
        if(this.form.barcodes.length === 0){
          isValid = false;
          this.errors.barcode = "Campo obrigatório";
        }          
      }

      if(this.configuration?.required_fields?.table_mat_med){
        if(!this.form.table_mat_med){
          isValid = false;
          this.errors.table_mat_med = "Campo obrigatório";
        }          
      }

      if(this.form.table_mat_med && !this.selectedProcedureMatMed){
        isValid = false;
        this.errors.material_medicine_id = "Campo obrigatório";
      }

      if(this.configuration?.required_fields?.material_medicine_id){
        if(!this.form.material_medicine_id){
          isValid = false;
          this.errors.material_medicine_id = "Campo obrigatório";
        }          
      }
      
      delete this.errors.temperature;
      
      if(this.form.cold_chain_medicine === '1' && !this.form.temperature){
          isValid = false;
          this.errors.temperature = "Campo obrigatório";
      } else {
        delete this.errors.temperature;
      }      

      if(this.configuration?.required_fields?.table){
        if(!this.form.table){
          isValid = false;
          this.errors.table = "Campo obrigatório";
        }          
      }

      if(this.configuration?.required_fields?.criticality_level){
        if(!this.selectedCriticalityLevel?.code){
          isValid = false;
          this.errors.criticality_level = "Campo obrigatório";
        }          
      }

      if(this.configuration?.required_fields?.expense_code){
        if(!this.selectedExpenseCode?.code){
          isValid = false;
          this.errors.expense_code = "Campo obrigatório";
        }          
      }

      if(this.configuration?.required_fields?.manufacturer_id){
        if(!this.selectedManufacturer?.id){
          isValid = false;
          this.errors.manufacturer_id = "Campo obrigatório";
        }          
      }
      
      if(this.configuration?.required_fields?.product_subclass_id){
        if(!this.selectedSubclass?.id){
          isValid = false;
          this.errors.product_subclass_id = "Campo obrigatório";
        }          
      }

      if(this.selectedManufacturer?.id && !this.selectedManufacturer?.status?.enable){
        this.$toast.warning("Fabricante inativo em configurações");      
        isValid = false;
        this.errors.manufacturer_id = "Fabricante inativo em configurações";    
      }

      this.validated = isValid;
      
      return Object.keys(this.errors).length === 0;
    },

    isValidState(model) {
      return !this.validated || (model !== 'undefined' && model !== null && model !== '');
    },

    isValidStateNumber(model) {
      return !this.validated || (model !== 'undefined' && model !== null && model > 0);
    },
    changeSelectedCategory(value) {
      this.form.product_category_id = value?.id;
      this.classes = value?.classes.filter(category => category?.status?.enable === true);
      this.selectedClass = {};
      this.selectedSubclass = {};
		},
    changeSelectedClass(value) {
      this.form.product_class_id = value?.id;
      this.subclasses = value?.subclasses.filter(category => category?.status?.enable === true);;
      this.selectedSubclass = {};
    },
    changeSelectedSubclass(value) {
      this.form.product_subclass_id = value.id;
    },
    onSelectedMatMedTable(value) {
      this.selectedProcedureMatMed = null;
      this.proceduresMatMed = [];
      this.form.material_medicine_id = null;     
      this.mat_med.code = null;
      this.mat_med.pmc = null;
      this.mat_med.pmc_fracao = null;
      this.mat_med.pf = null;
      this.mat_med.pf_fracao = null;
    },
    changeSelectedProcedureMatMed(value) {
      this.form.material_medicine_id = value.id;
    },
    async handleSearchMatMed(query) {
      if(this.form.table_mat_med) {
        const type = this.form.table_mat_med === 'Simpro' ? 'MAT_MED_SIMPRO' : 'MAT_MED_BRASINDICE';

        await apiMatMed.searchItems(this.clinic.id, type, query)
            .then(({ data }) => (this.proceduresMatMed = data))
            .catch(err => this.$toast.error(err.message));
      } else {
        this.$toast.warning("Selecione um tabela");
      }
    },
    handleChangeColdChainMedicine(value){
      if (this.form.cold_chain_medicine === '0') {
        this.form.temperature = '';
      }
    },
    onSelectMatMed(materialMedicine) {
      this.form.material_medicine_id = materialMedicine.id;

      if(materialMedicine.brasindice){
        this.mat_med.code = materialMedicine.brasindice.cod_tiss;

        if(!this.form.name || !this.form.id) this.form.name = materialMedicine.brasindice.item;

        if(materialMedicine.brasindice?.updates){
          const pfb = materialMedicine.brasindice.updates.find(item => item.flag_preco === 'PFB');
          const pmc = materialMedicine.brasindice.updates.find(item => item.flag_preco === 'PMC');

          this.mat_med.pf = parseNumberToMoney(0);
          this.mat_med.pf_fracao = parseNumberToMoney(0);
          this.mat_med.pmc = parseNumberToMoney(0);
          this.mat_med.pmc_fracao = parseNumberToMoney(0);

          if(pfb){
            this.mat_med.pf = parseNumberToMoney(pfb?.preco_apres);
            this.mat_med.pf_fracao = parseNumberToMoney(pfb?.preco_frac);
          }
          
          if(pmc) {            
            this.mat_med.pmc = parseNumberToMoney(pmc?.preco_apres);
            this.mat_med.pmc_fracao = parseNumberToMoney(pmc?.preco_frac);
          }          
        }
      }else {
        if(!this.form.name || !this.form.id) this.form.name = materialMedicine.simpro.descricao;

        if(materialMedicine.simpro?.updates[0]){
          this.mat_med.code = materialMedicine.simpro.cd_tuss;
            this.mat_med.pf = parseNumberToMoney(materialMedicine.simpro.updates[0]?.pc_em_fab);
            this.mat_med.pf_fracao = parseNumberToMoney(materialMedicine.simpro.updates[0]?.pc_fr_fab);
            this.mat_med.pmc = parseNumberToMoney(materialMedicine.simpro.updates[0]?.pc_em_ven);
            this.mat_med.pmc_fracao = parseNumberToMoney(materialMedicine.simpro.updates[0]?.pc_fr_ven);
        }
      }
      
    },
    changeSelectedExpenseCode(value) {
      this.form.expense_code = value.code;
    },
    changeSelectedTable(value) {
      this.form.table = value.code;
    },
    changeSelectedCriticalityLevel(value) {
      this.form.criticality_level = value.code;
    },
    changeSelectedManufacturer(value) {
      this.form.manufacturer_id = value.id;
    },
    handleInputOnlyNumbers(event) {
      event.target.value = event.target.value.replace(/[^0-9]/g, '');;
    },
    addBarcode(){
      if(this.newBarcode){
        if(!this.form.barcodes.find(item => item.barcode === this.newBarcode)){
          this.form.barcodes.push({'barcode': this.newBarcode, 'editable': false});
          this.newBarcode = null;
        } else {
          this.$toast.error('Código de barras já foi cadastrado!');
        }
      } else {
        this.$toast.error('Informe o código de barras!');
      }
    },    
    editBarcode(index){
      this.$set(this.form.barcodes[index], 'editable', true);
    },
    handleChangeBarcode(index) {
      this.$set(this.form.barcodes[index], 'editable', false);
    },
    deleteBarcode(index){
      this.form.barcodes.splice(index, 1);
    },
    formatTemperature(event) {      
      let valor = event.target.value.replace(/[^0-9º,-]/g, '');

      if (!valor.includes('ºC')) {
        valor = valor.replace('º', '') + 'º C';
      }
      
      event.target.value = valor;
    },
    isValidTemperature(value) {
      const regex = /^-?\d*\.?\d{0,1}$/;
      return regex.test(value);
    }
  },
  computed: {
    isValidName() {
      return this.form.name.length > 0;
    },

    invalidFeedbackName() {
      if (this.validated && (!this.form.name || this.form.name.length === 0)) {
        return 'Campo obrigatório';
      }
      return '';
    },

    isValidExpirationNotice() {
      return this.form.expiration_notice > 0;
    },

    invalidFeedbackExpirationNotice() {
      if (this.validated && (!this.form.expiration_notice || parseInt(this.form.expiration_notice) <= 0)) {
        return 'Campo obrigatório';
      }
      return '';
    },

    invalidFeedbackMinimumQuantity() {
      if (this.validated && (!this.form.minimum_quantity ||parseInt(this.form.minimum_quantity) <= 0)) {
        return 'Campo obrigatório';
      }
      return '';
    },

    isValidMinimumQuantity() {
      return this.form.minimum_quantity > 0;
    },
  },
  watch: {
    product: function(newValue) {
      if (newValue) {
        this.form = {
          ...newValue,
          batch_control: this.batchControlOptions.find(el => el.value === newValue.batch_control),
          table_mat_med: this.mat_med_tables.find(item => item.toLowerCase() === newValue.table_mat_med),
          temperature: newValue.temperature ? newValue.temperature + 'º C' : null,
          cold_chain_medicine: newValue.cold_chain_medicine ? newValue.cold_chain_medicine : 0,
          psychotropic_medication: newValue.psychotropic_medication ? newValue.psychotropic_medication : 0,
        };
        
        this.selectedCategory = newValue.category || {};
        this.selectedClass = newValue.class || {};
        this.selectedSubclass = newValue.subclass || {};
        this.selectedManufacturer = newValue.manufacturer || {};

        if(newValue.manufacturer && !newValue.manufacturer.status?.enable){
          this.$toast.warning("Fabricante inativo em configurações");          
        }
        
        if (newValue.material_medicine) {
          const { id, brasindice, simpro } = newValue.material_medicine;

          this.selectedProcedureMatMed = {
            id,
            type: this.form.table_mat_med,
            [brasindice ? 'brasindice' : 'simpro']: brasindice || simpro
          };

          this.onSelectMatMed(this.selectedProcedureMatMed);
        }

        this.selectedExpenseCode = this.expenseCodeOptions.find(item => item.code === newValue.expense_code) || {};
        this.selectedTable = this.tableOptions.find(item => item.code === newValue.table) || {};
        this.selectedCriticalityLevel = this.criticalityLevelOptions.find(item => item.code === newValue.criticality_level) || {};
      } else {
        this.clearForm();
      }
    },
  }
}
</script>
<style lang="scss">
#product-modal {
  .modal-content {
    top: 50%;
    left: 50%;
    transform: translate(-50%, 0);

    .modal-body {
      padding: 0;

      textarea {
        min-height: 100px;
      }
      
      .multiselect {
        .multiselect__single {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: calc(100% - 15px);
          
          div {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }

      .help-icon {
        svg {
          height: 20px;
          padding-bottom: 3px;
        }
      }

      .sub-title {
        font-size: 18px;
        font-weight: 700;
        line-height: 24.12px;
        letter-spacing: -0.01em;
        text-align: left;
        color: #305BF2;
      }

      .modal-header {
        width: 100%;
        display: inline-flex;
        align-items: center;
        justify-content: space-between;
        padding: 24px;

        svg {
          fill: var(--neutral-500);
        }
      }

      .modal-title {
        font-family: 'Nunito Sans';
        font-style: normal;
        font-weight: 600;
        font-size: 18px;
        line-height: 28px;
        color: var(--type-active);
      }

      .icon {
        width: 24px;
      }

      .md-button {
        text-align: right;
        margin: 24px 24px 24px 0;

        .button {
          font-family: 'Nunito Sans';
          font-style: normal;
          font-weight: 700;
          font-size: 16px;
          line-height: 24px;
          padding: 8px 16px;
        }
      }

      .row {
        margin: 0 !important;

        .title {
          font-family: 'Nunito Sans';
          font-style: normal;
          font-weight: 600;
          font-size: 16px;
          line-height: 150%;
          color: var(--dark-blue);
        }

        .optional {
          font-family: 'Nunito Sans';
          font-style: normal;
          font-weight: 600;
          font-size: 12px;
          line-height: 130%;
          align-items: center;
          color: var(--type-placeholder);
          padding-left: 3px;
        }

        .no-padding {
          padding: 0 !important;

          hr {
            margin: 16px 0 0 0;
          }
        }

        .form-group {
          margin: 0 !important;
        }

        .padding_24_24_0 {
          padding: 24px 24px 0 !important;
        }

        .padding_24_0_0_24 {
          padding: 24px 0px 0 24px !important;
        }

        .padding_16_0_0_24 {
          padding: 16px 0px 0 24px !important;
        }

        .padding_24_24_0_8 {
          padding: 24px 24px 0 8px !important;
        }

        .padding_16_24_0_8 {
          padding: 16px 24px 0 8px !important;
        }

        .padding_16_0_0_8 {
          padding: 16px 0 0 8px !important;
        }
      }

      .card {
        border-radius: 5px;

        &#barcodes{
          .input-group {
            border-bottom: 1px solid #C6CEEB !important;

            .input-group-append {
              padding: 7px;
            }

            input {
              border: none !important;
              border-radius: 0px !important;

              &:disabled {
                background-color: #FFF !important;
              }
            }

            .icon-container{
              width: 35px;
              height: auto;
              cursor: pointer;
              padding: 0px 10px;

              div {
                margin: auto;

                .icon {
                  width: 25px;
                }

                .icon-plus {
                  height: auto;
                  width: 25px;
                }

                .icon-trash {
                  height: auto;
                  width: 25px;

                  path {
                    stroke: #FBA59D;
                  }
                }
              }
            }
          }
        }

        .card-header {
          color: #0C1D59;
          font-size: 16px;
          font-weight: 600;
        }
      }

      .invalid-feedback {
        width: 100%;
        margin-top: 0.25rem;
        font-size: 80%;
        color: #dc3545;
      }
    }
  }
}
</style>
